// NEW_FILE_JUSTIFIED: Org admin page listing all policy application drafts — new route /insurance/drafts with no overlap to existing pages

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/
import { useTableSort } from '@/hooks/useTableSort';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import { SortableColumnHeader } from '@/components/insurance/SortableColumnHeader';
import { DRAFTS_COLUMNS } from '@/lib/tableColumns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DeleteConfirmDialog } from '@/components/insurance/DeleteConfirmDialog';
import { FileEdit, Pencil, Eye, Trash2 } from 'lucide-react';

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'rejected':
      return 'destructive';
    case 'under_review':
      return 'secondary';
    default:
      return 'outline';
  }
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function PolicyDraftsPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const orgId = currentOrg?._id as Id<'organisations'> | undefined; // AS_JUSTIFIED: currentOrg._id is typed as Id<'organisations'>

  const drafts = useQuery(
    api['insurance/policyApplications'].listDrafts,
    orgId ? { orgId } : 'skip'
  );

  const batchDelete = useMutation(api['insurance/policyApplications'].batchDeleteDrafts);

  const draftItems = (drafts ?? []).filter(d => d.status === 'draft');

  const draftAccessor = useCallback(
    (draft: (typeof draftItems)[number], colId: string): string | number => {
      switch (colId) {
        case 'application':
          return draft.customerName || draft.policyNumber;
        case 'insuranceType':
          return draft.insuranceTypeName ?? '';
        case 'status':
          return draft.status;
        case 'createdBy':
          return draft.creatorName ?? '';
        case 'lastUpdated':
          return draft.updatedAt;
        default:
          return '';
      }
    },
    []
  );

  const {
    sortState,
    toggleSort,
    sortedItems: sortedDrafts,
  } = useTableSort(
    draftItems,
    DRAFTS_COLUMNS,
    { defaultSort: { sortBy: 'lastUpdated', direction: 'desc' } },
    draftAccessor
  );

  const onDelete = useCallback(
    async (ids: string[]) => {
      await batchDelete({
        policyIds: ids as Id<'insurancePolicies'>[], // AS_JUSTIFIED: ids come from draft._id which are Id<'insurancePolicies'>
      });
    },
    [batchDelete]
  );

  const {
    selectedIds,
    isDeleteOpen,
    setIsDeleteOpen,
    handleSelectRow,
    handleSelectAll,
    handleDeleteConfirm,
    isAllSelected,
    isSomeSelected,
    selectedCount,
  } = useBatchSelection(draftItems, onDelete);

  if (orgLoading || drafts === undefined) {
    return (
      <div className="flex-1 p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Policy Drafts</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Draft, pending, and rejected policy applications
          </p>
        </div>
        {selectedCount > 0 && (
          <Button variant="destructive" size="sm" onClick={() => setIsDeleteOpen(true)}>
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete ({selectedCount})
          </Button>
        )}
      </div>

      {drafts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FileEdit className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground font-medium">No draft applications</p>
          <p className="text-sm text-muted-foreground mt-1">
            Saved policy application drafts will appear here
          </p>
        </div>
      ) : (
        <div className="rounded-none border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  {draftItems.length > 0 && (
                    <Checkbox
                      checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all drafts"
                    />
                  )}
                </TableHead>
                {DRAFTS_COLUMNS.map(col => (
                  <TableHead key={col.id}>
                    <SortableColumnHeader
                      column={col}
                      sortState={sortState}
                      onToggleSort={toggleSort}
                    />
                  </TableHead>
                ))}
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDrafts.map(draft => {
                const canResume = draft.status === 'draft' || draft.status === 'rejected';
                const isDraft = draft.status === 'draft';
                return (
                  <TableRow key={draft._id}>
                    <TableCell>
                      {isDraft && (
                        <Checkbox
                          checked={selectedIds.has(draft._id)}
                          onCheckedChange={checked => handleSelectRow(draft._id, !!checked)}
                          aria-label={`Select ${draft.customerName || draft.policyNumber}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {draft.customerName || draft.policyNumber}
                    </TableCell>
                    <TableCell>{draft.insuranceTypeName ?? '\u2014'}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(draft.status)}>
                        {statusLabel(draft.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {draft.creatorName ?? '\u2014'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(draft.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => navigate(`/insurance/policies/${draft._id}`)}
                      >
                        {canResume ? (
                          <>
                            <Pencil className="h-3.5 w-3.5" /> Resume
                          </>
                        ) : (
                          <>
                            <Eye className="h-3.5 w-3.5" /> View
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <DeleteConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        count={selectedCount}
        entityLabel="draft"
      />
    </div>
  );
}
